import React, { useState } from 'react'
import { Pie } from '@visx/shape'
import { ProvidedProps, PieArcDatum } from '@visx/shape/lib/shapes/Pie'
import { Group } from '@visx/group'
import { animated, useTransition, to } from '@react-spring/web'
import { localPoint } from '@visx/event'

// Generic data interface
interface ChartData {
  label: string
  value: number
  color: string
}

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 }

export type DonutChartProps = {
  width?: number
  height?: number
  margin?: typeof defaultMargin
  animate?: boolean
  data: ChartData[]
  backgroundColor?: string
  donutThickness?: number
  backgroundPadding?: number
  backgroundDonutColor?: string
  legendWidth?: number
}

type TooltipData = {
  label: string
  value: number
  x: number
  y: number
}

export function DonutChart({
  width = 200,
  height = 200,
  margin = defaultMargin,
  animate = true,
  data,
  backgroundColor = '#FFF',
  donutThickness = 40,
  legendWidth = 150,
}: DonutChartProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [isMouseInChart, setIsMouseInChart] = useState(false)
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null)

  if (width < 10) return null

  // Calculate total for percentage calculation
  const totalValue = data.reduce((sum, item) => sum + item.value, 0)

  // Animation for legend items
  const legendTransitions = useTransition(data, {
    from: animate ? { opacity: 0, x: -20 } : { opacity: 1, x: 0 },
    enter: { opacity: 1, x: 0 },
    trail: 100, // Stagger the animations
    config: { tension: 300, friction: 20 },
    keys: (item) => item.label,
  })

  const truncateLabel = (label: string, maxLength: number): string => {
    if (label.length <= maxLength) return label
    return label.substring(0, maxLength - 3) + '...'
  }

  // Calculate truncate length based on legend width
  // Rough estimate: ~6-8px per character for text-xs, plus space for icon (16px) and value (40px)
  const availableTextWidth = legendWidth - 16 - 40 - 16 // icon + value + gaps
  const calculatedTruncateLength = Math.max(Math.floor(availableTextWidth / 7), 5) // min 5 chars

  // Calculate dimensions to fit legend and chart within total width/height
  const chartContainerWidth = width - legendWidth // Remaining space for chart
  const chartSize = Math.min(chartContainerWidth, height) // Use available space
  const chartRadius = Math.max((chartSize - margin.left - margin.right) / 2, 20) // Minimum radius
  const chartCenterY = chartSize / 2
  const chartCenterX = chartSize / 2

  return (
    <div className="relative" style={{ width, height }}>
      <div className="absolute top-0 left-0 flex flex-col overflow-y-auto pr-4" style={{ width: legendWidth, height }}>
        {legendTransitions((style, item) => (
          <animated.div key={item.label} className="flex items-center gap-2 mb-2" style={style}>
            <div
              className="w-4 h-4 shrink-0 rounded-sm"
              style={{
                backgroundColor: item.color,
              }}
            />
            <span className="text-xs flex-1 truncate" title={item.label}>
              {truncateLabel(item.label, calculatedTruncateLength)}
            </span>
            <span className="text-xs text-right min-w-[40px]">{new Intl.NumberFormat().format(item.value)}</span>
          </animated.div>
        ))}
      </div>

      <div
        className="absolute top-0 flex items-start justify-center"
        style={{
          left: legendWidth,
          width: chartContainerWidth,
          height,
        }}
        onMouseEnter={() => setIsMouseInChart(true)}
        onMouseLeave={() => {
          setIsMouseInChart(false)
          setHoveredSegment(null)
        }}
      >
        <svg width={chartSize} height={chartSize}>
          <rect rx={14} width={chartSize} height={chartSize} fill={backgroundColor} />
          <Group top={chartCenterY + margin.top} left={chartCenterX + margin.left}>
            {/* Main donut chart */}
            <Pie
              data={data}
              pieValue={(d) => d.value}
              outerRadius={chartRadius}
              innerRadius={chartRadius - donutThickness}
              cornerRadius={1}
              padAngle={0.025}
              padRadius={chartRadius}
            >
              {(pie) => (
                <AnimatedPie<ChartData>
                  {...pie}
                  animate={animate}
                  getKey={(arc) => arc.data.label}
                  getColor={(arc) => arc.data.color}
                  isMouseInChart={isMouseInChart}
                  hoveredSegment={hoveredSegment}
                  showTooltip={(event, arc) => {
                    const point = localPoint(event) || { x: 0, y: 0 }
                    // Calculate the chart's actual position within the container
                    const chartOffset = (chartContainerWidth - chartSize) / 2
                    setTooltipData({
                      label: arc.data.label,
                      value: arc.data.value,
                      x: point.x + chartOffset,
                      y: point.y,
                    })
                    setHoveredSegment(arc.data.label)
                  }}
                  hideTooltip={() => {
                    setTooltipData(null)
                    // Don't reset hoveredSegment here to maintain hover state when moving between segments
                  }}
                />
              )}
            </Pie>
          </Group>
        </svg>

        {tooltipData && (
          <div
            className="absolute bg-white text-primary shadow-md rounded p-2 pointer-events-none z-10"
            style={{
              top: tooltipData.y,
              left: tooltipData.x + 30,
              transform: 'translate(-50%, -100%)',
              fontSize: '14px',
              border: '1px solid bg-primary',
              whiteSpace: 'nowrap',
            }}
          >
            <div className="text-primary mb-1">{tooltipData.label}</div>
            <div className="flex items-center gap-2 text-secondary">
              <div
                className="w-4 h-4 shrink-0 rounded-sm"
                style={{
                  backgroundColor: data.find((item) => item.label === tooltipData.label)?.color,
                }}
              />
              <span>
                {tooltipData.label}&nbsp;
                {new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(
                  tooltipData.value / totalValue
                )}
              </span>
            </div>
            {/* Arrow pointing down */}
            <div
              className="absolute w-3 h-3 bg-primary border-r border-b rotate-45"
              style={{
                bottom: '-6px',
                left: '50%',
                marginLeft: '-6px',
                borderColor: 'bg-primary',
              }}
            ></div>
          </div>
        )}
      </div>
    </div>
  )
}

// react-spring transition definitions
type AnimatedStyles = { startAngle: number; endAngle: number; opacity: number }

const fromLeaveTransition = ({ endAngle }: PieArcDatum<any>) => ({
  startAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  endAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  opacity: 0,
})

const enterUpdateTransition = ({ startAngle, endAngle }: PieArcDatum<any>) => ({
  startAngle,
  endAngle,
  opacity: 1,
})

type AnimatedPieProps<Datum> = ProvidedProps<Datum> & {
  animate?: boolean
  getKey: (d: PieArcDatum<Datum>) => string
  getColor: (d: PieArcDatum<Datum>) => string
  isMouseInChart: boolean
  hoveredSegment: string | null
  showTooltip: (event: React.MouseEvent, d: PieArcDatum<Datum>) => void
  hideTooltip: () => void
}

function AnimatedPie<Datum>({
  animate,
  arcs,
  path,
  getKey,
  getColor,
  isMouseInChart,
  hoveredSegment,
  showTooltip,
  hideTooltip,
}: AnimatedPieProps<Datum>) {
  const transitions = useTransition<PieArcDatum<Datum>, AnimatedStyles>(arcs, {
    from: animate ? fromLeaveTransition : enterUpdateTransition,
    enter: enterUpdateTransition,
    update: enterUpdateTransition,
    leave: animate ? fromLeaveTransition : enterUpdateTransition,
    keys: getKey,
    config: { tension: 120, friction: 14 },
    trail: 100,
  })

  return transitions((props, arc, { key }) => {
    const isHovered = hoveredSegment === getKey(arc)

    // Calculate opacity based on mouse position and hover state
    const segmentOpacity = isMouseInChart
      ? isHovered
        ? 1
        : 0.2 // When mouse is in chart: hovered = 100%, others = 20%
      : 1 // When mouse is outside chart: all = 100%

    return (
      <g key={key}>
        <animated.path
          d={to([props.startAngle, props.endAngle], (startAngle, endAngle) =>
            path({
              ...arc,
              startAngle,
              endAngle,
            })
          )}
          fill={getColor(arc)}
          opacity={to([props.opacity], (baseOpacity) => baseOpacity * segmentOpacity)}
          className="transition-opacity duration-200"
          onMouseMove={(event) => showTooltip(event, arc)}
          onMouseLeave={() => hideTooltip()}
        />
      </g>
    )
  })
}
